import { PROMPTS } from './prompts';
import { DEFAULT_FORMULAS } from './formulas';

const KEYS = {
  candidates: 'ic_candidates',
  branding:   'ic_branding',
  tiers:      'ic_tiers',
  prompts:    'ic_prompts',
  formulas:   'ic_formulas',
  results:    'ic_results',
  essays:     'ic_essays',
};

function load(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function save(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

export const DEFAULT_BRANDING = {
  logo:    'IELTSCOMPASS',
  tagline: 'with Mr. Ahmed Salem',
  motto:   process.env.REACT_APP_MOTTO || 'Navigate your path to success.',
  phone1:  '00201008182425',
  phone2:  '00971565727742',
  announcement: {
    active:    false,
    message:   'New IELTS group starting Monday 18th October — limited places available!',
    groupDate: '',
    color:     'gold',
    showTo:    'visitors_and_trial',
  },
};

// ── SESSION PERSISTENCE ──────────────────────────────────────────
const SESSION_KEY = 'ic_session';
const SESSION_TTL = 24 * 60 * 60 * 1000; // 24 hours

export function saveSession(candidate, role) {
  save(SESSION_KEY, { candidate, role, timestamp: Date.now() });
}

export function loadSession() {
  try {
    const v = localStorage.getItem(SESSION_KEY);
    if (!v) return null;
    const s = JSON.parse(v);
    if (!s?.timestamp || !s?.candidate) return null;
    if (Date.now() - s.timestamp > SESSION_TTL) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s; // { candidate, role: 'candidate'|'teacher', timestamp }
  } catch { return null; }
}

export function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

// ── TIER DEFINITIONS ────────────────────────────────────────────
// limitSmart : max Smart Writing essays  (9999 = unlimited)
// limitTest  : max Test Mode essays      (9999 = unlimited)
// Trial gets exactly 1 of each, then WhatsApp lead capture
export const DEFAULT_TIERS = {
  trial:    { label:'Trial',    limitSmart:1,    limitTest:1    },
  standard: { label:'Standard', limitSmart:9999, limitTest:12   },
  premium:  { label:'Premium',  limitSmart:9999, limitTest:25   },
};

export const DEFAULT_RESULTS = [
  { id:1, name:'Sara Mohamed',  score:'Band 7.0', date:'March 2025',   note:'Target was 6.5 — exceeded by 0.5 bands!' },
  { id:2, name:'Omar Khalil',   score:'Band 7.5', date:'January 2025', note:'Improved from Band 5.5 in just 3 months.' },
];

export const DEFAULT_ESSAYS = [
  {
    id:1, type:'model', essayType:'discuss',
    title:'Student Rewards — Model Essay',
    author:'Mr. Ahmed Salem', score:'Band 8.0', date:'2025',
    text:`In recent years, the question of how to recognise student achievement has become an increasingly debated issue in education. While some argue that only those who attain the highest scores deserve recognition, others contend that consistent improvement is a more meaningful basis for reward. Although both views carry merit, I firmly believe that rewarding progress is ultimately more beneficial for long-term academic development.\n\nOn the one hand, those who support merit-based rewards argue that academic excellence deserves recognition. This is because high grades reflect sustained discipline and effort, which means rewarding them sends a clear message about the value of hard work. For instance, in Egypt, top-performing students are frequently awarded scholarships that incentivise others to strive for academic success. Although this view seems reasonable, it may overlook the majority of learners who work diligently but will never reach the top percentile.\n\nOn the other hand, those who advocate for progress-based rewards maintain that improvement is a more equitable and motivationally effective standard. This is because recognising individual growth acknowledges diverse starting points, which means students at every level remain motivated to improve. Research in Jordanian secondary schools found that progress-tracking systems led to measurable reductions in student disengagement. Thus, progress-based reward is not only more inclusive but also better supported by educational evidence.\n\nHaving considered both perspectives, it is evident that each approach carries genuine merit. Although neither is without limitation, progress-based recognition ultimately offers the more inclusive and psychologically sound framework. As student populations grow increasingly diverse, a more equitable approach to recognising achievement will become indispensable.`,
  },
];

export const DEMO_CANDIDATES = [
  { email:'ahmed@trial.com',   name:'Ahmed Hassan',  tier:'trial',    usedSmart:0, usedTest:0, active:true },
  { email:'sara@standard.com', name:'Sara Mohamed',  tier:'standard', usedSmart:3, usedTest:2, active:true },
  { email:'omar@premium.com',  name:'Omar Khalil',   tier:'premium',  usedSmart:0, usedTest:0, active:true },
];

// ── PUBLIC API ──────────────────────────────────────────────────
export function loadBranding()    { return load(KEYS.branding,   DEFAULT_BRANDING); }
export function saveBranding(v)   { save(KEYS.branding, v); }

export function loadTiers()       { return load(KEYS.tiers,      DEFAULT_TIERS); }
export function saveTiers(v)      { save(KEYS.tiers, v); }

export function loadCandidates()  { return load(KEYS.candidates, DEMO_CANDIDATES); }
export function saveCandidates(v) { save(KEYS.candidates, v); }

export function loadPrompts()     { return load(KEYS.prompts,    PROMPTS); }
export function savePrompts(v)    { save(KEYS.prompts, v); }

export function loadFormulas()    { return load(KEYS.formulas,   DEFAULT_FORMULAS); }
export function saveFormulas(v)   { save(KEYS.formulas, v); }

export function loadResults()     { return load(KEYS.results,    DEFAULT_RESULTS); }
export function saveResults(v)    { save(KEYS.results, v); }

export function loadEssays()      { return load(KEYS.essays,     DEFAULT_ESSAYS); }
export function saveEssays(v)     { save(KEYS.essays, v); }

// ── IELTS SESSIONS ──────────────────────────────────────────────
const IELTS_SESSIONS_KEY = 'ic_ielts_sessions';

export const DEFAULT_IELTS_SESSIONS = [
  { id:1, days:['Mon','Tue','Wed','Thu'], startTime:'20:00', endTime:'22:00', timezone:'UAE (Gulf Standard Time GMT+4)', label:'Evening Group', status:'active' },
  { id:2, days:['Mon','Tue','Wed','Thu'], startTime:'10:00', endTime:'12:00', timezone:'UAE (Gulf Standard Time GMT+4)', label:'Morning Group (Summer)', status:'inactive' },
];

export function loadIeltsSessions() { return load(IELTS_SESSIONS_KEY, DEFAULT_IELTS_SESSIONS); }
export function saveIeltsSessions(v) { save(IELTS_SESSIONS_KEY, v); }

// ── ANNOUNCEMENT BANNER ─────────────────────────────────────────
const ANNOUNCEMENT_KEY = 'ic_announcement';

export const DEFAULT_ANNOUNCEMENT = {
  active: true,
  message: '🔔 New IELTS group starting Monday — limited places available! Contact Mr. Ahmed to reserve your spot.',
  target: 'all',           // 'all' | 'ielts' | 'academy' | 'everyone'
  startDate: '',
  endDate: '',
  color: 'gold',           // 'gold' | 'navy' | 'green' | 'red'
  showWAButton: true,
  waButtonText: 'Reserve My Place',
  waNumber: '201008182425',
};

export function loadAnnouncement()    { return load(ANNOUNCEMENT_KEY, DEFAULT_ANNOUNCEMENT); }
export function saveAnnouncement(v)   { save(ANNOUNCEMENT_KEY, v); }

export function getCandidate(email) {
  return loadCandidates().find(c => c.email.toLowerCase() === email.toLowerCase().trim()) || null;
}

export function incrementUsed(email, mode) {
  const list    = loadCandidates();
  const updated = list.map(c => {
    if (c.email.toLowerCase() !== email.toLowerCase()) return c;
    if (mode === 'test') return { ...c, usedTest:(c.usedTest||0)+1 };
    return { ...c, usedSmart:(c.usedSmart||0)+1 };
  });
  saveCandidates(updated);
}

// ── ACCESS HELPERS ──────────────────────────────────────────────

export function canWriteSmart(candidate, tiers) {
  if (!candidate || !candidate.active) return false;
  const t = tiers[candidate.tier];
  if (!t) return false;
  const limit = t.limitSmart ?? t.limit ?? 1;
  return limit >= 9999 || (candidate.usedSmart||0) < limit;
}

export function canWriteTest(candidate, tiers) {
  if (!candidate || !candidate.active) return false;
  const t = tiers[candidate.tier];
  if (!t) return false;
  const limit = t.limitTest ?? 1;
  return limit >= 9999 || (candidate.usedTest||0) < limit;
}

export function remainingSmart(candidate, tiers) {
  const t = tiers[candidate?.tier];
  if (!t) return 0;
  const limit = t.limitSmart ?? t.limit ?? 1;
  if (limit >= 9999) return 'Unlimited';
  return Math.max(0, limit - (candidate?.usedSmart||0));
}

export function remainingTest(candidate, tiers) {
  const t = tiers[candidate?.tier];
  if (!t) return 0;
  const limit = t.limitTest ?? 1;
  if (limit >= 9999) return 'Unlimited';
  return Math.max(0, limit - (candidate?.usedTest||0));
}

export function isTrial(candidate) {
  return candidate?.tier === 'trial';
}

export function isPaidTier(candidate) {
  return candidate?.tier === 'standard' || candidate?.tier === 'premium';
}
